import type { TurnMessage, TurnToolCall } from './global-ai-turn-step-stream';

/**
 * Accumulates the message history for one assistant turn of the tool loop:
 * prior conversation turns (immutable) plus the messages this turn produces
 * (the user prompt, then each assistant tool-call step and its tool result).
 * `messages()` returns the full array to post on the next step.
 */
export class TurnState {
  private readonly priorMessages: TurnMessage[];
  private readonly turnMessages: TurnMessage[] = [];

  constructor(priorMessages: TurnMessage[], userPrompt: string) {
    this.priorMessages = priorMessages;
    this.turnMessages.push({ role: 'user', content: userPrompt });
  }

  messages(): TurnMessage[] {
    return [...this.priorMessages, ...this.turnMessages];
  }

  recordAssistantStep(streamedText: string, toolCall: TurnToolCall): void {
    const text = streamedText.trim();
    this.turnMessages.push(
      text.length > 0
        ? { role: 'assistant', content: text, toolCalls: [toolCall] }
        : { role: 'assistant', toolCalls: [toolCall] }
    );
  }

  recordToolResult(toolCallId: string, content: string): void {
    this.turnMessages.push({ role: 'tool', toolCallId, content });
  }
}

/** Turn-local cache for large step artifacts the model references by id. */
export class ArtifactCache {
  private readonly byId = new Map<string, unknown>();

  put(id: string, payload: unknown): void {
    this.byId.set(id, payload);
  }

  get(id: string): unknown {
    return this.byId.get(id);
  }
}
